import React, { useState } from 'react';
import { Search, FileText, Link, AlertCircle, Check, X, ChevronDown, ChevronUp, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import apiClient from '@/lib/apiClient';

/**
 * DocumentSearch component for searching and debugging ingested documents.
 * Read-only - allows searching by URL, title, or content.
 * onOpenChunkInspector(doc): optional callback to open full Chunk Inspector for a document.
 */
export default function DocumentSearch({ isDark = false, onOpenChunkInspector }) {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('url');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [docChunks, setDocChunks] = useState({});
  const [loadingChunks, setLoadingChunks] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await apiClient.get('/search/documents', {
        params: {
          q: query.trim(),
          search_type: searchType,
          include_chunks: true,
          limit: 50
        }
      });
      setResults(response.data);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.detail || 'Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckUrl = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await apiClient.get('/search/check-url', {
        params: { url: query.trim() }
      });
      // Convert to search results format
      if (response.data.found) {
        setResults({
          documents: [response.data.document],
          chunks: [],
          total_documents: 1,
          total_chunks: response.data.chunks_count,
          query: query,
          search_type: 'url'
        });
      } else {
        setResults({
          documents: [],
          chunks: [],
          total_documents: 0,
          total_chunks: 0,
          query: query,
          search_type: 'url'
        });
        setError('URL nicht im Index gefunden');
      }
    } catch (err) {
      console.error('Check URL error:', err);
      setError(err.response?.data?.detail || 'Fehler bei der URL-Prüfung');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentChunks = async (documentId) => {
    if (docChunks[documentId]) {
      // Already loaded, just toggle expansion
      setExpandedDoc(expandedDoc === documentId ? null : documentId);
      return;
    }

    setLoadingChunks(documentId);
    try {
      const response = await apiClient.get(`/search/document/${documentId}/debug`);
      setDocChunks(prev => ({
        ...prev,
        [documentId]: response.data
      }));
      setExpandedDoc(documentId);
    } catch (err) {
      console.error('Load chunks error:', err);
      setError(`Fehler beim Laden der Chunks: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingChunks(null);
    }
  };

  const getStatusBadge = (status, isActive) => {
    if (!isActive) {
      return <Badge variant="secondary" className="bg-gray-200 text-gray-700">Inaktiv</Badge>;
    }
    switch (status) {
      case 'indexed':
        return <Badge className="bg-green-100 text-green-800">Indiziert</Badge>;
      case 'failed':
        return <Badge variant="destructive">Fehlgeschlagen</Badge>;
      case 'processing':
      case 'indexing':
        return <Badge className="bg-yellow-100 text-yellow-800">Verarbeitung</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
      <CardHeader>
        <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Search className="h-5 w-5" />
          Suche
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="URL, Titel oder Text eingeben..."
              className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
            />
          </div>
          <Select value={searchType} onValueChange={setSearchType}>
            <SelectTrigger className={`w-[140px] ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}>
              <SelectValue placeholder="Suchtyp" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="url">URL</SelectItem>
              <SelectItem value="title">Titel</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="all">Alles</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={loading || !query.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Suchen</span>
          </Button>
          {searchType === 'url' && (
            <Button type="button" variant="outline" onClick={handleCheckUrl} disabled={loading || !query.trim()}>
              <Link className="h-4 w-4 mr-2" />
              URL prüfen
            </Button>
          )}
        </form>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {results.total_documents} Dokument(e) gefunden
              {results.total_chunks > 0 && `, ${results.total_chunks} Chunk(s)`}
            </div>

            {/* No results */}
            {results.documents.length === 0 && (
              <div className={`p-4 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Keine Dokumente gefunden für "{results.query}"</p>
                <p className="text-xs mt-1">Prüfen Sie die URL oder versuchen Sie einen anderen Suchtyp</p>
              </div>
            )}

            {/* Document List */}
            <div className="max-h-[400px] overflow-y-auto border rounded-lg p-2" style={{ scrollBehavior: 'smooth' }}>
              <div className="space-y-2">
                {results.documents.map((doc) => (
                  <Collapsible
                    key={doc.id}
                    open={expandedDoc === doc.id}
                    onOpenChange={() => loadDocumentChunks(doc.id)}
                  >
                    <div className={`border rounded-lg ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                      <CollapsibleTrigger asChild>
                        <div className={`p-3 cursor-pointer hover:bg-slate-50 ${isDark ? 'hover:bg-slate-700' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <FileText className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                                <span className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {doc.title}
                                </span>
                                {getStatusBadge(doc.status, doc.is_active)}
                                <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                              </div>
                              {doc.source_url && (
                                <div className={`text-xs mt-1 truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {doc.source_url}
                                </div>
                              )}
                              <div className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                {doc.chunks_count} Chunks | ID: {doc.id.substring(0, 8)}...
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {loadingChunks === doc.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : expandedDoc === doc.id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {docChunks[doc.id] && (
                          <div className={`p-3 border-t ${isDark ? 'border-slate-600 bg-slate-750' : 'border-slate-200 bg-slate-50'}`}>
                            {/* Open full Chunk Inspector (when callback provided) */}
                            {onOpenChunkInspector && (
                              <div className="mb-3">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onOpenChunkInspector(doc)}
                                  className={isDark ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  Chunks inspizieren (vollständig)
                                </Button>
                              </div>
                            )}
                            {/* Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                <span className="font-medium">Chunks:</span> {docChunks[doc.id].stats.total_chunks}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                <span className="font-medium">Ø Länge:</span> {Math.round(docChunks[doc.id].stats.avg_chunk_length)} Zeichen
                              </div>
                              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                <span className="font-medium">Min:</span> {docChunks[doc.id].stats.min_chunk_length}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                <span className="font-medium">Max:</span> {docChunks[doc.id].stats.max_chunk_length}
                              </div>
                            </div>

                            {/* Error message if present */}
                            {doc.error_message && (
                              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                <strong>Fehler:</strong> {doc.error_message}
                              </div>
                            )}

                            {/* Chunk preview */}
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                              {docChunks[doc.id].chunks.slice(0, 10).map((chunk, idx) => (
                                <div
                                  key={chunk.id}
                                  className={`p-2 rounded text-xs ${isDark ? 'bg-slate-700' : 'bg-white'} border ${isDark ? 'border-slate-600' : 'border-slate-200'}`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                      Chunk {chunk.chunk_index} {chunk.section && `(${chunk.section})`}
                                    </span>
                                    <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                                      {chunk.text_length} Zeichen
                                    </span>
                                  </div>
                                  <div className={`whitespace-pre-wrap break-words ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    {chunk.text.substring(0, 300)}
                                    {chunk.text.length > 300 && '...'}
                                  </div>
                                </div>
                              ))}
                              {docChunks[doc.id].chunks.length > 10 && (
                                <div className={`text-center text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                  ... und {docChunks[doc.id].chunks.length - 10} weitere Chunks
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
